import type { GreenEnergyMetrics } from "@/types";
import type { MaterialItem } from "@/store/materialsStore";

// kg CO₂e per unit of each material (indicative embodied-carbon factors).
const CARBON_FACTOR: Record<string, number> = {
  concrete: 260, // per m³
  rebar: 1850, // per t
  brick: 0.42, // per unit
  timber: -120, // per m³ (sequestered, net negative)
  roofing: 9.8, // per m²
  glazing: 28, // per m²
  cement: 46, // per 50kg bag
  sand: 6, // per m³
  plaster: 3.1, // per m²
};

const FLOOR_AREA_M2 = 240;
const BASELINE_KG_PER_M2 = 650;
const SOLAR = { roofPitchDeg: 27, orientation: "North", annualYieldKwh: 9860, passiveGainPct: 18 };

export function deriveGreenMetrics(items: MaterialItem[]): GreenEnergyMetrics {
  const embodiedCarbonKg = Math.round(items.reduce((sum, m) => sum + m.quantity * (CARBON_FACTOR[m.id] ?? 0), 0));
  const carbonPerM2 = Math.round(embodiedCarbonKg / FLOOR_AREA_M2);
  const compliancePct = Math.max(40, Math.min(99, Math.round(115 - (carbonPerM2 / BASELINE_KG_PER_M2) * 45)));

  const concrete = items.find((m) => m.id === "concrete");
  const concreteShare = concrete ? (concrete.quantity * CARBON_FACTOR.concrete) / Math.max(embodiedCarbonKg, 1) : 0;

  return {
    compliancePct,
    embodiedCarbonKg,
    carbonPerM2,
    solar: SOLAR,
    ecoSwaps: [
      {
        from: "Concrete 25MPa (OPC)",
        to: "30% fly-ash blended concrete",
        reductionPct: Math.round(concreteShare * 30),
        note: "Same strength class · SANS 10100 compliant · no cost premium at current suppliers",
      },
    ],
  };
}
